import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";

import {
  useCopyFrames,
  useFramesInfinite,
  type FrameFilters,
  type FrameListing,
} from "@/api/zero/frames";
import { SETTINGS_FIELD_MAX_WIDTH } from "@/design-tokens/layout";
import { SettingsSection } from "@/features/Settings/components/SettingsSection";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import { FramePreviewDialog } from "@/features/ZeroAdmin/frames/FramePreviewDialog";
import { CopyFramesStore } from "@/features/ZeroAdmin/frames/store";
import { SiteAutocomplete } from "@/features/ZeroAdmin/sites/SiteAutocomplete";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { SearchIcon } from "@/icons";
import { CHANNEL_MEDIUMS } from "@/types/dragon-tiles";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/copy-frames",
)({
  component: CloneFramesPage,
});

function frameMedia(frame: FrameListing): string[] {
  return [...new Set((frame.frame_variants ?? []).map((v) => v.medium))].sort();
}

function CloneFramesPage() {
  const copyFrames = useCopyFrames();

  useEffect(() => {
    CopyFramesStore.actions.reset();
  }, []);

  const originSiteId = CopyFramesStore.useOriginSiteId();
  const destSiteId = CopyFramesStore.useDestSiteId();
  const selectedFrameIds = CopyFramesStore.useSelectedFrameIds();

  const [search, setSearch] = useState("");
  const [mediumFilter, setMediumFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [previewFrame, setPreviewFrame] = useState<FrameListing | null>(null);

  useEffect(() => {
    setSearch("");
    setMediumFilter("");
    setStatusFilter("");
  }, [originSiteId]);

  const debouncedSearch = useDebouncedValue(search, 300);

  const filters = useMemo<FrameFilters>(
    () => ({
      medium: mediumFilter || undefined,
      approvalStatus: statusFilter || undefined,
      search: debouncedSearch.trim() || undefined,
    }),
    [mediumFilter, statusFilter, debouncedSearch],
  );

  const {
    data,
    isLoading: framesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFramesInfinite(originSiteId, filters);

  const frameList = useMemo(
    () => data?.pages.flatMap((p) => p.frames) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  // Status has no canonical enum in connect-api, so options come from the
  // statuses present in the frames loaded so far.
  const allStatuses = useMemo(() => {
    const set = new Set<string>();
    frameList.forEach((f) => {
      if (f.approval_status) set.add(f.approval_status);
    });
    if (statusFilter) set.add(statusFilter);
    return [...set].sort();
  }, [frameList, statusFilter]);

  const loadedIds = frameList.map((f) => f.rj_id);
  const allLoadedSelected =
    frameList.length > 0 &&
    frameList.every((f) => selectedFrameIds.includes(f.rj_id));

  const sameSite = originSiteId !== "" && originSiteId === destSiteId;
  const disabled =
    originSiteId === "" ||
    destSiteId === "" ||
    sameSite ||
    selectedFrameIds.length === 0 ||
    copyFrames.isPending;

  const handleCopy = () => {
    if (disabled) return;
    copyFrames.mutate(
      { originSiteId, destSiteId, frameIds: selectedFrameIds },
      {
        onSuccess: (result) => {
          enqueueSnackbar(
            `Copied ${result.copied_frames} tiles and ` +
              `${result.copied_frame_variants} variants — ` +
              `${result.skipped_existing} already existed, ` +
              `${result.skipped_no_inner_tile} had no matching tile`,
            { variant: "success" },
          );
          CopyFramesStore.actions.clearSelection();
        },
        onError: (error) => enqueueErrorSnackbar(error, "Failed to copy tiles"),
      },
    );
  };

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Admin" }]}
      title="Copy tiles"
      description="Copy tiles (and all their variants) from one site to another, matched to dragon and inner tiles by name."
      data-testid="frame-clone-page"
    >
      <Stack sx={{ gap: 4 }}>
        <Alert severity="info" data-testid="frame-clone-match-note">
          Tiles that already exist on the destination, or whose tile has no name
          match there, are skipped. Copies land inactive and pending review.
        </Alert>

        <SettingsSection title="Source and destination">
          <Stack sx={{ gap: 3, maxWidth: SETTINGS_FIELD_MAX_WIDTH }}>
            <SiteAutocomplete
              label="Origin site"
              value={originSiteId}
              onChange={(rjId) => CopyFramesStore.actions.setOrigin(rjId)}
              isConnectOnly
              testId="frame-clone-origin-select"
            />

            <SiteAutocomplete
              label="Destination site"
              value={destSiteId}
              onChange={(rjId) => CopyFramesStore.actions.setDest(rjId)}
              isConnectOnly
              error={sameSite}
              helperText={
                sameSite ? "Origin and destination must differ" : undefined
              }
              testId="frame-clone-dest-select"
            />
          </Stack>
        </SettingsSection>

        {originSiteId !== "" && (
          <SettingsSection title="Tiles to copy">
            {framesLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading tiles…
              </Typography>
            ) : frameList.length === 0 && filtersAreEmpty(filters) ? (
              <Typography variant="body2" color="text.secondary">
                This site has no tiles.
              </Typography>
            ) : (
              <Stack sx={{ gap: 2, maxWidth: 640 }}>
                <Stack
                  direction="row"
                  sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
                >
                  <TextField
                    size="small"
                    placeholder="Search by name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="frame-clone-search"
                    sx={{ flexGrow: 1, minWidth: 200 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    select
                    label="Medium"
                    value={mediumFilter}
                    onChange={(e) => setMediumFilter(e.target.value)}
                    data-testid="frame-clone-medium-filter"
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="">All media</MenuItem>
                    {CHANNEL_MEDIUMS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="frame-clone-status-filter"
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    {allStatuses.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack
                  direction="row"
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="frame-clone-count"
                  >
                    {selectedFrameIds.length} selected · {frameList.length} of{" "}
                    {total} loaded
                  </Typography>
                  <Button
                    size="small"
                    disabled={frameList.length === 0}
                    onClick={() =>
                      allLoadedSelected
                        ? CopyFramesStore.actions.deselectMany(loadedIds)
                        : CopyFramesStore.actions.selectMany(loadedIds)
                    }
                    data-testid="frame-clone-select-all"
                  >
                    {allLoadedSelected ? "Clear loaded" : "Select all loaded"}
                  </Button>
                </Stack>
                <Divider />

                {frameList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No tiles match your filters.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
                    <Stack divider={<Divider />}>
                      {frameList.map((frame) => (
                        <Stack
                          key={frame.rj_id}
                          direction="row"
                          sx={{ alignItems: "center", gap: 1, py: 2 }}
                        >
                          <FormControlLabel
                            sx={{ flexGrow: 1, mr: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedFrameIds.includes(frame.rj_id)}
                                onChange={() =>
                                  CopyFramesStore.actions.toggleFrame(
                                    frame.rj_id,
                                  )
                                }
                                data-testid={`frame-clone-frame-${frame.rj_id}`}
                              />
                            }
                            label={frame.name}
                          />
                          <Stack direction="row" sx={{ gap: 2 }}>
                            {frameMedia(frame).map((m) => (
                              <Chip
                                key={m}
                                size="small"
                                variant="outlined"
                                label={m}
                              />
                            ))}
                          </Stack>
                          <Button
                            size="small"
                            onClick={() => setPreviewFrame(frame)}
                            data-testid={`frame-clone-preview-${frame.rj_id}`}
                          >
                            Preview
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                {hasNextPage && (
                  <Button
                    size="small"
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                    data-testid="frame-clone-load-more"
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {isFetchingNextPage ? "Loading…" : "Load more"}
                  </Button>
                )}
              </Stack>
            )}
          </SettingsSection>
        )}

        <Button
          variant="contained"
          disabled={disabled}
          onClick={handleCopy}
          data-testid="frame-clone-submit"
          sx={{ alignSelf: "flex-start" }}
        >
          {copyFrames.isPending ? "Copying…" : "Copy tiles"}
        </Button>

        <FramePreviewDialog
          frame={previewFrame}
          onClose={() => setPreviewFrame(null)}
        />
      </Stack>
    </SettingsPageLayout>
  );
}

function filtersAreEmpty(filters: FrameFilters): boolean {
  return !filters.medium && !filters.approvalStatus && !filters.search;
}
